import type { JSX } from "react";
import Svg, { Circle, Path, Rect } from "react-native-svg";

import { DEFAULT_BRAND, frame, type IconProps } from "./icon-props";

// Per-credential glyphs — Phosphor/Lucide-shaped paths with a uniform
// 1.7px stroke, round joins, round caps. Path data lifted from the
// design refresh reference (docs/design_refresh/reference/App
// Prototype.html and Marketing Site.html) so the app matches the
// designer's spec without pulling in a full icon dependency.

export function TruckIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Rect
        x={3}
        y={11}
        width={13}
        height={6}
        rx={1}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M16 13h3l2 3v1h-5z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Circle
        cx={7}
        cy={18}
        r={1.6}
        stroke={color}
        strokeWidth={strokeWidth}
      />
      <Circle
        cx={18}
        cy={18}
        r={1.6}
        stroke={color}
        strokeWidth={strokeWidth}
      />
    </Svg>
  );
}

export function AnchorIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Path
        d="M5 16h14l-2 4H7z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M12 4v12"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M12 7l5 2"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

export function DroneIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Circle
        cx={12}
        cy={12}
        r={3}
        stroke={color}
        strokeWidth={strokeWidth}
      />
      <Path
        d="M5 5l3 3M19 5l-3 3M5 19l3-3M19 19l-3-3"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

export function HouseIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Path
        d="M4 11l8-6 8 6"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M6 11v8h12v-8"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M10 19v-5h4v5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

// Generic credential icon for verticals without a custom shape.
export function CertificateIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Path
        d="M12 3l7 3v6c0 4-3 7-7 9-4-2-7-5-7-9V6z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

// EPA 608 — refrigerant handling. Snowflake = HVAC's universal mark.
export function SnowflakeIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Path
        d="M12 3v18M4 7.5l16 9M4 16.5l16-9"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <Path
        d="M12 6l-1.5-1.5M12 6l1.5-1.5M12 18l-1.5 1.5M12 18l1.5 1.5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  );
}

// NASM personal trainer. Dumbbell.
export function DumbbellIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Path
        d="M3 10v4M5 7v10M8 9v6M16 9v6M19 7v10M21 10v4M8 12h8"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  );
}

// PTCB pharmacy tech. Pill capsule.
export function PillIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Path
        d="M7.05 16.95L17 7a4.95 4.95 0 1 1 7 7l-9.95 9.95a4.95 4.95 0 0 1-7-7z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        transform="translate(-3,-3) scale(0.9)"
      />
      <Path
        d="M9 9l6 6"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  );
}

// MBLEx massage. Stylized lotus / wellness mark.
export function LotusIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Path
        d="M12 17c-4 0-7-2-7-2 2-4 5-4 7 0 2-4 5-4 7 0 0 0-3 2-7 2z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M12 15c0-3-1.5-5-3-7 3 0 4 3 4 5M12 15c0-3 1.5-5 3-7-3 0-4 3-4 5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

// Real estate salesperson. Building variant — taller than house.
export function BuildingIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Path
        d="M5 21V5h9v16M14 21V11h5v10M5 21h14"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M8 9h3M8 13h3M8 17h3M16 14h1.5M16 18h1.5"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  );
}

// Notary. Document stamp / seal.
export function StampIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Path
        d="M9 4h6l-1 6h-4z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M5 13h14v3a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path
        d="M5 21h14"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  );
}

// Insurance adjuster. Umbrella.
export function UmbrellaIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Path
        d="M3.5 12a8.5 8.5 0 0 1 17 0"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <Path
        d="M12 3.5V12M12 12v6a2.5 2.5 0 0 1-5 0"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <Path
        d="M7.5 12a4.5 4.5 0 0 1 9 0"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  );
}

// CTEC California Tax Preparer. Ledger / receipt.
export function LedgerIcon({
  size = 24,
  color = DEFAULT_BRAND,
  strokeWidth = 1.7,
}: IconProps): JSX.Element {
  return (
    <Svg {...frame({ size })} fill="none">
      <Path
        d="M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <Path
        d="M9 8h6M9 12h6M9 16h4"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </Svg>
  );
}

// Slug → icon registry. New verticals fall back to CertificateIcon
// until a custom one is registered.
export function getCredentialIcon(
  slug: string,
): (props: IconProps) => JSX.Element {
  switch (slug) {
    case "cdl":
      return TruckIcon;
    case "oupv":
    case "uscg-100t-master":
    case "uscg-able-seaman":
      return AnchorIcon;
    case "faa-part-107":
    case "faa-adx":
    case "faa-foi":
      return DroneIcon;
    case "nmls-safe-mlo":
      return HouseIcon;
    case "insurance-producer":
      return UmbrellaIcon;
    case "irs-enrolled-agent":
    case "ctec-tax-preparer":
      return LedgerIcon;
    case "epa-608":
      return SnowflakeIcon;
    case "nasm-personal-trainer":
      return DumbbellIcon;
    case "ptcb-pharmacy-tech":
      return PillIcon;
    case "mblex-massage":
      return LotusIcon;
    case "real-estate":
    case "real-estate-salesperson":
      return BuildingIcon;
    case "notary":
    case "notary-public":
      return StampIcon;
    case "insurance-adjuster":
      return UmbrellaIcon;
    default:
      return CertificateIcon;
  }
}
